import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface TranscriptSegment {
  id: string;
  speaker: 'interviewer' | 'candidate';
  text: string;
  timestamp: number;
}

export interface TranscriptView {
  segments: TranscriptSegment[];
  interviewerSegments: number;
  candidateSegments: number;
  syncedAt: string | null;
}

export interface CoachingMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string;
}

export interface CoachingThread {
  id: string;
  title: string;
  createdAt: string;
  messages: CoachingMessage[];
}

export interface SessionDetail {
  transcript: TranscriptView | null;
  coachingThreads: CoachingThread[];
}

interface RawSeg {
  id: string;
  text: string;
  timestamp: number;
}

async function fetchSessionDetail(companyId: string, sessionId: string): Promise<SessionDetail> {
  const neurax = supabase.schema('neurax' as never);

  // 1. Transcript (one row, RLS-scoped to company)
  const transcriptQ = neurax
    .from('interview_transcripts' as never)
    .select(
      'id, interview_session_id, transcript_data, total_segments, interviewer_segments, you_segments, synced_at'
    )
    .eq('interview_session_id', sessionId)
    .eq('b2b_company_id', companyId)
    .maybeSingle();

  // 2. Coaching threads for this session
  const conversationsQ = neurax
    .from('conversations' as never)
    .select('id, title, created_at')
    .eq('interview_session_id', sessionId)
    .order('created_at', { ascending: true });

  const [transcriptRes, convRes] = await Promise.all([transcriptQ, conversationsQ]);

  if (transcriptRes.error) throw new Error(`interview_transcripts: ${transcriptRes.error.message}`);
  if (convRes.error) throw new Error(`conversations: ${convRes.error.message}`);

  // Build transcript view
  let transcript: TranscriptView | null = null;
  const tRow = transcriptRes.data as {
    transcript_data: { interviewer?: RawSeg[]; you?: RawSeg[] } | null;
    interviewer_segments: number | null;
    you_segments: number | null;
    synced_at: string | null;
  } | null;

  if (tRow && tRow.transcript_data) {
    const interviewer = (tRow.transcript_data.interviewer ?? []).map((s) => ({
      id: s.id,
      speaker: 'interviewer' as const,
      text: s.text,
      timestamp: s.timestamp,
    }));
    const candidate = (tRow.transcript_data.you ?? []).map((s) => ({
      id: s.id,
      speaker: 'candidate' as const,
      text: s.text,
      timestamp: s.timestamp,
    }));
    const segments = [...interviewer, ...candidate].sort((a, b) => a.timestamp - b.timestamp);

    transcript = {
      segments,
      interviewerSegments: tRow.interviewer_segments ?? interviewer.length,
      candidateSegments: tRow.you_segments ?? candidate.length,
      syncedAt: tRow.synced_at,
    };
  }

  // 3. Messages — only when there are conversations
  const conversations = (convRes.data ?? []) as {
    id: string;
    title: string | null;
    created_at: string;
  }[];
  if (conversations.length === 0) return { transcript, coachingThreads: [] };

  const conversationIds = conversations.map((c) => c.id);
  const { data: messageRows, error: msgErr } = await neurax
    .from('messages' as never)
    .select('id, conversation_id, role, content, created_at')
    .in('conversation_id', conversationIds)
    .order('created_at', { ascending: true });

  if (msgErr) throw new Error(`messages: ${msgErr.message}`);

  const messages = (messageRows ?? []) as {
    id: string;
    conversation_id: string;
    role: string;
    content: string;
    created_at: string;
  }[];

  const messagesByThread: Record<string, CoachingMessage[]> = {};
  for (const m of messages) {
    if (!messagesByThread[m.conversation_id]) messagesByThread[m.conversation_id] = [];
    messagesByThread[m.conversation_id].push({
      id: m.id,
      role: m.role,
      content: m.content,
      createdAt: m.created_at,
    });
  }

  const coachingThreads: CoachingThread[] = conversations.map((c) => ({
    id: c.id,
    title: c.title ?? 'Untitled thread',
    createdAt: c.created_at,
    messages: messagesByThread[c.id] ?? [],
  }));

  return { transcript, coachingThreads };
}

export function useSessionDetail(sessionId: string | null) {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'session-detail', companyId, sessionId],
    queryFn: () => fetchSessionDetail(companyId, sessionId as string),
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}
